import React, { useState, useEffect } from 'react';
import { Gift, Mail, CheckCircle, Sparkles } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';

interface VipLaunchBannerProps {
  endsAt: string;
  userId?: string;
  isEmailVerified?: boolean;
}

const VipLaunchBanner: React.FC<VipLaunchBannerProps> = ({
  endsAt,
  userId,
  isEmailVerified = false
}) => {
  const [bonusClaimed, setBonusClaimed] = useState(false);
  const [bonusAmount, setBonusAmount] = useState(3);
  const [loading, setLoading] = useState(true);
  const [vipLaunchActive, setVipLaunchActive] = useState(true);

  useEffect(() => {
    if (userId) {
      checkBonusStatus();
      checkVipLaunchStatus();
    } else {
      setLoading(false);
    }
  }, [userId]);

  const checkVipLaunchStatus = async () => {
    const { data } = await supabase
      .from('app_settings')
      .select('vip_launch_active, vip_launch_bonus_credits, standard_email_bonus_credits')
      .limit(1)
      .maybeSingle();

    if (data) {
      setVipLaunchActive(data.vip_launch_active ?? true);
      setBonusAmount(data.vip_launch_active ? (data.vip_launch_bonus_credits ?? 3) : (data.standard_email_bonus_credits ?? 1));
    }
  };

  const checkBonusStatus = async () => {
    if (!userId) return;

    const { data } = await supabase
      .from('profiles')
      .select('email_bonus_claimed, email_bonus_amount')
      .eq('id', userId)
      .maybeSingle();

    if (data) {
      setBonusClaimed(data.email_bonus_claimed ?? false);
      if (data.email_bonus_amount) {
        setBonusAmount(data.email_bonus_amount);
      }
    }
    setLoading(false);
  };

  if (loading) {
    return null;
  }

  // If bonus already claimed, show success message
  if (bonusClaimed) {
    return (
      <div className="mb-4 rounded-lg p-3 sm:p-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 text-white shadow-lg">
        <div className="flex items-center gap-2">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm sm:text-base font-semibold">
              Success! Your {bonusAmount} FREE Vybe credit{bonusAmount !== 1 ? 's have' : ' has'} been added
            </div>
            <div className="text-xs opacity-95 mt-0.5">
              Start creating memorable experiences with your Vybe credits
            </div>
          </div>
          <Sparkles className="w-6 h-6 flex-shrink-0 animate-pulse" />
        </div>
      </div>
    );
  }

  // If email not verified, show claim incentive
  if (!isEmailVerified) {
    return (
      <div className="mb-4 rounded-lg p-3 sm:p-4 bg-gradient-to-r from-[#3a1c71] via-[#d76d77] to-[#ffaf7b] text-white shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-white/20 flex items-center justify-center backdrop-blur-sm">
            <Gift className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm sm:text-base font-bold mb-1 flex items-center gap-2">
              {vipLaunchActive ? (
                <>
                  <Sparkles className="w-4 h-4 animate-pulse" />
                  VIP Launch Bonus: Claim {bonusAmount} FREE Vybe Credits
                </>
              ) : (
                <>Verify Your Email for {bonusAmount} FREE Vybe Credit{bonusAmount !== 1 ? 's' : ''}</>
              )}
            </div>
            <div className="text-xs sm:text-sm opacity-95 mb-2">
              Check your inbox and click the verification link to unlock your bonus
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Mail className="w-3.5 h-3.5" />
              <span className="font-medium">Didn't receive it? Check your spam folder</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If email verified but bonus somehow not claimed yet
  return null;
};

export default VipLaunchBanner;